/**
 * Окошко «Название комнаты»: открывается кликом по подписи комнаты на плане.
 * Своё название («Кухня») заменяет номер «ПО N» на плане, в карте помещений и в 3D;
 * пустое поле возвращает номер. Переименование — правка чертежа: Ctrl+Z его откатит.
 */
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { useProjectStore } from "@/shared/store";
import { useRoomNamesStore } from "@/shared/store/layers";
import { UIKit } from "@/shared/ui";

/** Длиннее подпись не влезает в комнату на плане. */
const MAX_ROOM_NAME_LENGTH = 40;

const roomNameSchema = z.object({
	name: z.string().trim().max(MAX_ROOM_NAME_LENGTH, `Не длиннее ${MAX_ROOM_NAME_LENGTH} символов`),
});

interface Props {
	roomId: string;
	/** Номер комнаты по умолчанию («ПО 3») — подсказка в пустом поле */
	defaultName: string;
	onClose: () => void;
}

export const RoomNameDialog = ({ roomId, defaultName, onClose }: Props) => {
	const recordEdit = useProjectStore((s) => s.recordEdit);
	const currentName = useRoomNamesStore((s) => s.items[roomId]?.name ?? "");
	const setRoomName = useRoomNamesStore((s) => s.setRoomName);
	const form = useForm({
		defaultValues: { name: currentName },
		validators: { onChange: roomNameSchema },
		onSubmit: ({ value }) => {
			recordEdit(() => setRoomName(roomId, value.name));
			onClose();
		},
	});

	return (
		<UIKit.Modal ariaLabel="Название комнаты" onClose={onClose} className="p-5 md:max-w-sm">
			<form action={() => form.handleSubmit()}>
				<UIKit.Text size="large" className="font-semibold">
					Название комнаты
				</UIKit.Text>
				<div className="mt-4">
					<UIKit.FormField form={form} name="name">
						{(field) => (
							<UIKit.TextInput
								label="Название"
								name={field.name}
								value={field.state.value}
								onChange={field.handleChange}
								placeholder={defaultName}
								maxLength={MAX_ROOM_NAME_LENGTH}
								autoComplete="off"
								autoFocus
							/>
						)}
					</UIKit.FormField>
				</div>
				<UIKit.Text className="mt-2 text-xs text-muted">
					Оставьте пустым, чтобы вернуть «{defaultName}».
				</UIKit.Text>
				<div className="mt-5 flex justify-end gap-2">
					<UIKit.Button className="px-3 py-2 text-sm" onClick={onClose}>
						Отмена
					</UIKit.Button>
					<UIKit.Button
						type="submit"
						className="border-accent bg-accent px-3 py-2 text-sm font-medium text-overlay-ink hover:border-accent-hover hover:bg-accent-hover"
					>
						Сохранить
					</UIKit.Button>
				</div>
			</form>
		</UIKit.Modal>
	);
};
